import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import classNames from 'classnames';

import type { IconComponent } from '@/components/atoms/SvgIcon/SvgIcon';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';

import styles from './ModalComponent.module.scss';

interface ModalHeaderProps {
    titleId: string;
    title: ReactNode;
    titleIcon?: IconComponent;
    /** Shows the close button; it only reports the click, the modal decides whether to close. */
    onClose?: () => void;
}

/** The title row. The title names the dialog for assistive technology. */
export function ModalHeader(props: ModalHeaderProps) {
    const { titleId, title, titleIcon, onClose } = props;
    const { t } = useTranslation();

    return (
        <div
            data-modal-part="header"
            className={classNames(styles.header, {
                [styles.withTitleIcon]: titleIcon !== undefined,
            })}>
            {/* An h5, as the modal title has always been, so the heading outline stays put. */}
            <Dialog.Title asChild>
                <h5 id={titleId} data-modal-part="title" className={styles.title}>
                    {titleIcon !== undefined && (
                        <TitleIcon icon={titleIcon} className={styles.titleIcon} />
                    )}
                    {title}
                </h5>
            </Dialog.Title>
            {onClose ? (
                <button
                    type="button"
                    data-modal-part="close"
                    className={styles.close}
                    aria-label={t('molecules.modalComponent.close')}
                    onClick={onClose}>
                    <span aria-hidden="true">×</span>
                </button>
            ) : null}
        </div>
    );
}

interface ModalSectionProps {
    className?: string;
    children?: ReactNode;
}

/** The padded content section, for use inside a `ModalComponent` composed from children. */
export function ModalBody(props: ModalSectionProps) {
    const { className, children } = props;
    return (
        <div data-modal-part="body" className={classNames(styles.body, className)}>
            {children}
        </div>
    );
}

/** The actions row at the bottom, for use inside a `ModalComponent` composed from children. */
export function ModalFooter(props: ModalSectionProps) {
    const { className, children } = props;
    return (
        <div data-modal-part="footer" className={classNames(styles.footer, className)}>
            {children}
        </div>
    );
}

/**
 * Keeps an action at the footer's start, such as Cancel in a wizard or a reset beside Save,
 * while the rest stay at the end. Put it first in the footer.
 */
export function ModalFooterStart(props: { children: ReactNode }) {
    return <div className={styles.footerStart}>{props.children}</div>;
}
